import { QueriesObserver, QueryClient } from "@tanstack/react-query";
import { startTransition, useEffect } from "react";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { Build } from "~/components/build/Show/lib/types";
import {
  allStepsQueryKey,
  stepsByStateQueryKey,
  topLevelStepsQueryKey,
} from "~/components/build/Show/lib/queries/steps";
import { StateOrder } from "~/components/build/Show/lib/filterConstants";
import { Step } from "~/lib/pipeline";

/**
 * Subscribes to steps related queries and updates the Zustand store with the latest data.
 */
export default function useSyncStepsToStore(queryClient: QueryClient, build: Build | undefined) {
  const setSteps = useBuildStore((state) => state.setSteps);

  useEffect(() => {
    if (!Features.ProgressiveBuildShow || !build) {
      return;
    }

    // Keep track of the last updated data for each query to avoid unnecessary updates to the store.
    const queriesDataUpdatedAt = new Map();

    // Watch top-level steps related queries and jam em' into the store.
    const stepQueryKeys = [
      allStepsQueryKey(build.id),
      topLevelStepsQueryKey(build.id),
      ...StateOrder.map((state) => stepsByStateQueryKey(build.id, state)),
    ];

    const observer = new QueriesObserver(
      queryClient,
      stepQueryKeys.map((queryKey) => ({
        queryKey,
        // Don't execute the query, just listen for changes
        enabled: false,
      })),
    );

    const unsubscribe = observer.subscribe((results) => {
      results.forEach((result, index) => {
        const queryKey = stepQueryKeys[index];
        const prevDataUpdatedAt = queriesDataUpdatedAt.get(queryKey) || 0;

        // Check if the data has been updated since the last time we received it before
        // updating the store and triggering re-renders.
        if (result.dataUpdatedAt > prevDataUpdatedAt) {
          queriesDataUpdatedAt.set(queryKey, result.dataUpdatedAt);
          startTransition(() => {
            setSteps(result.data as Step[], build);
          });
        }
      });
    });

    return unsubscribe;
  }, []);
}
